import { ExternalLink, Leaf, Dna, Landmark, CloudSun } from "lucide-react";
import { trackSpiritClick } from "../lib/analytics";
import { IMG } from "../data/content";

/**
 * SpiritCallout — modern partner CTA block for the SPIRIT™ platform.
 *
 * Props:
 *   variant: "full" | "condensed"   (default: "condensed")
 *   sectionLabel: string             (default: "Provenance Intelligence")
 *   showDomains: boolean             (default: true for full, false for condensed)
 *   id: string                       optional anchor id
 *   source: string                   GA4 event source (e.g. "home", "intelligence", "genetics")
 */
export default function SpiritCallout({
  variant = "condensed",
  sectionLabel = "Provenance Intelligence",
  showDomains,
  id = "spirit",
  source = "unknown",
}) {
  const isFull = variant === "full";
  const renderDomains = showDomains ?? isFull;

  return (
    <section
      id={id}
      data-testid={`spirit-callout-${variant}`}
      className="scroll-mt-24 py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="relative rounded-3xl bg-eucalypt text-chalk overflow-hidden">
          {/* Subtle grain / dot pattern */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.06]"
            aria-hidden="true"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, #F9F6F0 1px, transparent 0)",
              backgroundSize: "22px 22px",
            }}
          />

          <div className="relative p-8 md:p-12 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              {/* Copy */}
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-6 flex-wrap">
                  <span className="inline-flex items-center gap-2 rounded-full border border-ochre/50 bg-ochre/10 text-ochre px-3 py-1 font-mono text-[10px] tracking-[0.22em] uppercase font-medium">
                    New · In Pilot
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-chalk/60">
                    {sectionLabel}
                  </span>
                </div>

                <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02]">
                  SPIRIT
                  <sup className="text-[0.28em] align-super tracking-normal ml-1 font-mono font-normal">
                    TM
                  </sup>
                </h2>
                <p className="mt-4 font-mono text-[11px] tracking-[0.22em] uppercase text-ochre/90">
                  Seed Provenance Intelligence &amp; Restoration Information Technology
                </p>

                <p className="mt-8 font-display italic text-2xl md:text-3xl text-chalk leading-snug max-w-2xl">
                  The future of restoration depends on understanding more than seed.
                </p>

                {isFull ? (
                  <>
                    <p className="mt-8 text-base md:text-lg text-chalk/85 leading-relaxed max-w-2xl">
                      SPIRIT is an integrated provenance intelligence platform developed by
                      SeedKeepers to support the collection, management and analysis of
                      provenance, population genetics, ecological metadata, cultural
                      governance and climate adaptation datasets associated with native
                      seed resources.
                    </p>
                    <p className="mt-5 text-base md:text-lg text-chalk/85 leading-relaxed max-w-2xl">
                      Designed for Traditional Owner organisations, ranger groups, seed
                      collectors, researchers and restoration practitioners, SPIRIT brings
                      together four critical knowledge domains.
                    </p>
                  </>
                ) : (
                  <p className="mt-8 text-base md:text-lg text-chalk/85 leading-relaxed max-w-2xl">
                    SPIRIT is an integrated provenance intelligence platform developed by
                    SeedKeepers — bringing ecological, genetic, cultural and climate
                    datasets together so practitioners can make better decisions about
                    native seed and restoration.
                  </p>
                )}
              </div>

              {/* iPad photo + CTA */}
              <div className="lg:col-span-5">
                <div
                  className="rounded-2xl overflow-hidden ring-1 ring-chalk/15 shadow-[0_16px_48px_-16px_rgba(0,0,0,0.5)] bg-chalk/5"
                  data-testid="spirit-callout-screenshot"
                >
                  <img
                    src={IMG.collectionApp}
                    alt="SPIRIT — Seed Provenance Intelligence platform running on an iPad in the field"
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </div>
                <p className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-chalk/50 text-center">
                  SPIRIT<sup className="text-[0.7em] align-super">TM</sup> on iPad · in the field · seedkeepers.app
                </p>

                <div className="mt-8 border-t border-chalk/15 pt-8">
                  <p className="specimen-label text-chalk/70 mb-3">Registered Partner Access</p>
                  <p className="text-sm md:text-base text-chalk/85 leading-relaxed">
                    Currently available to approved SeedKeepers partners, Traditional
                    Owner organisations, ranger groups, research institutions and
                    restoration practitioners participating in pilot programs.
                  </p>
                  <a
                    href="https://seedkeepers.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackSpiritClick(source, "primary_cta")}
                    data-testid="spirit-callout-cta"
                    className="group mt-6 inline-flex items-center justify-center gap-3 w-full bg-ochre text-ink rounded-full px-7 py-4 font-mono text-[11px] tracking-[0.28em] uppercase font-semibold hover:bg-chalk hover:text-eucalypt-dark transition-all"
                  >
                    Access SPIRIT
                    <ExternalLink
                      size={14}
                      className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                    />
                  </a>
                  <p className="mt-4 text-xs text-chalk/50 italic leading-relaxed text-center">
                    Available to registered SeedKeepers partners and pilot participants.
                  </p>
                </div>
              </div>
            </div>

            {/* Four knowledge domains — full variant only */}
            {renderDomains && (
              <div className="mt-14 md:mt-20 pt-10 border-t border-chalk/15">
                <p className="specimen-label text-chalk/70 mb-6">Four knowledge domains</p>
                <div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
                  data-testid="spirit-callout-domains"
                >
                  {[
                    {
                      no: "01",
                      icon: Leaf,
                      title: "Ecological Intelligence",
                      body: "Site characteristics, species associations, environmental conditions and collection metadata.",
                    },
                    {
                      no: "02",
                      icon: Dna,
                      title: "Genetic Intelligence",
                      body: "Provenance information, population diversity, germplasm records and genetic observations.",
                    },
                    {
                      no: "03",
                      icon: Landmark,
                      title: "Cultural Intelligence",
                      body: "Traditional Owner authority, cultural permissions, knowledge governance and stewardship obligations.",
                    },
                    {
                      no: "04",
                      icon: CloudSun,
                      title: "Climate Intelligence",
                      body: "Climate resilience indicators, environmental change monitoring and adaptation planning.",
                    },
                  ].map((d) => {
                    const Icon = d.icon;
                    return (
                      <div
                        key={d.no}
                        data-testid={`spirit-domain-${d.no}`}
                        className="rounded-2xl bg-chalk/5 ring-1 ring-chalk/10 p-6 flex flex-col gap-4 min-h-[220px] hover:bg-chalk/10 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[11px] tracking-[0.28em] text-ochre font-semibold">
                            {d.no}
                          </span>
                          <Icon size={20} className="text-ochre/80" />
                        </div>
                        <h3 className="font-display text-xl md:text-2xl leading-tight font-medium">{d.title}</h3>
                        <p className="text-sm text-chalk/75 leading-relaxed">{d.body}</p>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-10 text-sm md:text-base text-chalk/70 leading-relaxed max-w-3xl italic">
                  SPIRIT is being developed as a secure platform to strengthen
                  expert-led restoration, support evidence-based decision making and
                  build the knowledge infrastructure needed for future conservation and
                  climate adaptation programs.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
